// src/components/EmailGuideModal.js
import React, { useState } from 'react';
import { useLandingTranslation } from '../hooks/useLandingTranslation';
import axios from 'axios';

const EmailGuideModal = ({ show, onHide }) => {
  const { t } = useLandingTranslation('basic');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Reset message
    setMessage({ type: '', text: '' });
    
    // Validation
    if (!email.trim()) {
      setMessage({ type: 'error', text: t('email_guide.email_required') });
      return;
    }
    
    if (!validateEmail(email.trim())) {
      setMessage({ type: 'error', text: t('email_guide.email_invalid') });
      return;
    }

    try {
      setIsSubmitting(true);
      
      // Send email guide request to backend using existing endpoint
      const response = await axios.post('/api/full-guide-email', {
        email: email.trim()
      });

      if (response.data.success) {
        setMessage({ type: 'success', text: t('email_guide.success_message') });
        setEmail('');
        
        // Close modal after showing success message for 2 seconds
        setTimeout(() => {
          onHide();
          setMessage({ type: '', text: '' });
        }, 2000);
      } else {
        throw new Error(response.data.message || 'Unknown error');
      }
    } catch (error) {
      console.error('Error sending email guide:', error);
      setMessage({ type: 'error', text: t('email_guide.error_message') });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setMessage({ type: '', text: '' });
    onHide();
  };

  if (!show) return null;

  return (
    <div 
      className="email-modal" 
      style={{ 
        display: 'block',
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        zIndex: 1050,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
      onClick={(e) => e.target === e.currentTarget && handleClose()}
    >
      <div 
        className="email-modal-content"
        style={{
          backgroundColor: 'white',
          borderRadius: '15px',
          padding: '2rem',
          maxWidth: '500px',
          width: '90%',
          position: 'relative',
          boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
          textAlign: 'center'
        }}
      >
        <button 
          className="email-modal-close"
          onClick={handleClose}
          style={{
            position: 'absolute',
            top: '15px',
            right: '20px',
            background: 'none',
            border: 'none',
            fontSize: '24px',
            cursor: 'pointer',
            color: '#999'
          }}
        >
          ×
        </button>
        
        <h3 style={{ color: '#2c3e50', marginBottom: '1rem', fontSize: '1.5rem' }}>
          🎁 {t('email_guide.modal_title')}
        </h3>
        
        <p style={{ color: '#666', marginBottom: '1.5rem', lineHeight: '1.5' }}>
          {t('email_guide.description')}
        </p>
        
        <form className="email-form" onSubmit={handleSubmit}>
          <input 
            type="email" 
            className="email-input"
            placeholder={t('email_guide.email_placeholder')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isSubmitting}
            required
            style={{
              width: '100%',
              padding: '12px 20px',
              borderRadius: '25px',
              border: '2px solid #e0e0e0',
              fontSize: '16px',
              marginBottom: '1rem',
              outline: 'none',
              transition: 'border-color 0.3s'
            }}
          />
          
          <button 
            type="submit" 
            className="email-submit-btn"
            disabled={isSubmitting}
            style={{
              width: '100%',
              padding: '12px 20px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '25px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              opacity: isSubmitting ? 0.7 : 1,
              transition: 'all 0.3s'
            }}
          >
            {isSubmitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                {t('email_guide.sending')}
              </>
            ) : (
              <>
                📧 {t('email_guide.send_button')}
              </>
            )}
          </button>
        </form>
        
        {message.text && message.type === 'success' && (
          <div 
            className="email-success"
            style={{
              marginTop: '1rem',
              padding: '10px',
              backgroundColor: '#d4edda',
              color: '#155724',
              borderRadius: '8px',
              fontSize: '14px'
            }}
          >
            ✅ {message.text}
          </div>
        )}
        
        {message.text && message.type === 'error' && (
          <div 
            className="email-error"
            style={{
              marginTop: '1rem',
              padding: '10px',
              backgroundColor: '#f8d7da',
              color: '#721c24',
              borderRadius: '8px',
              fontSize: '14px'
            }}
          >
            ❌ {message.text}
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailGuideModal;